import { useState } from 'react';
import { format } from 'date-fns';
import type { NetworkType, ParamTreeNode } from '@mls-wallet/shared-types';
import { DecimalsButton } from '../../transaction-builder/parameter-inputs/DecimalsButton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  isLikelyAnEpochTime,
  TIMESTAMP_LIKELIHOOD,
} from '@/lib/timestampUtils';

/**
 * Check if value is max int for its type
 */
function isMaxInt(value: bigint, rawType: string): boolean {
  const match = rawType.match(/uint(\d+)/);
  if (!match || !match[1]) return false;
  // Bit shift (1 << n) computes 2^n in pure BigInt arithmetic,
  // avoiding Number conversion errors with large bit sizes like uint256
  const bits = BigInt(match[1]);
  const maxVal = (1n << bits) - 1n;
  return value === maxVal;
}

/**
 * Check if value is min int for its type
 */
function isMinInt(value: bigint, rawType: string): boolean {
  const match = rawType.match(/int(\d+)/);
  if (!match || !match[1] || rawType.startsWith('uint')) return false;
  // Bit shift (1 << n) computes 2^n in pure BigInt arithmetic,
  // avoiding Number conversion errors with large bit sizes like uint256
  const bits = BigInt(match[1]);
  const minVal = -(1n << (bits - 1n));
  return value === minVal;
}

/**
 * Format number with decimals (e.g., wei to ETH)
 * Uses BigInt arithmetic to avoid scientific notation for large decimals
 */
function formatNumber(num: string, decimals: number): string | null {
  // Handle null/empty/invalid inputs
  if (!num || num === '' || num === 'undefined' || num === 'null') return null;

  try {
    const bigIntValue = scientificNumberStringToBigInt(num);
    const valueAsBigInt =
      typeof bigIntValue === 'bigint' ? bigIntValue : BigInt(bigIntValue);

    // Use BigInt arithmetic throughout to avoid Number conversion
    // Build divisor without exponentiation to avoid Math.pow transpilation
    const divisor = BigInt('1' + '0'.repeat(decimals));
    const whole = valueAsBigInt / divisor;
    const remainder = valueAsBigInt % divisor;

    // Format whole part with commas
    const wholeWithCommas = whole
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    if (decimals === 0) return wholeWithCommas;

    // Format decimal part with leading zeros, then trim trailing zeros
    const remainderStr = remainder.toString().padStart(decimals, '0');
    const trimmed = remainderStr.replace(/0+$/, '');
    return trimmed ? `${wholeWithCommas}.${trimmed}` : wholeWithCommas;
  } catch (error) {
    console.error('Error in formatNumber', { error, num });
    return null;
  }
}

/**
 * Converts scientific notation string to bigint
 */
function scientificNumberStringToBigInt(str: string): bigint | string {
  if (!str.includes('e')) return str;

  const parts = str.split('e');
  let baseStr = parts[0];
  let powStr = parts[1];
  if (!baseStr || !powStr) return str;

  powStr = powStr.slice(1);
  let powBi = BigInt(powStr);

  // Case: Base is a decimal
  if (baseStr.includes('.')) {
    const decimalParts = baseStr.split('.');
    const float = decimalParts[1];
    baseStr = baseStr.replace('.', '');
    if (float) {
      powBi = powBi - BigInt(float.length);
    }
  }

  const base = BigInt(baseStr);
  // Build power of 10 without exponentiation to avoid Math.pow transpilation
  const pow = BigInt('1' + '0'.repeat(Number(powBi)));
  return base * pow;
}

/**
 * Format timestamp for display
 */
function FormattedTimestamp(props: {
  rawTimestampValue: string;
  excludeSeconds?: boolean;
}) {
  // Safely convert to number - very large values aren't valid timestamps anyway
  const timestampSeconds = (() => {
    try {
      const num = Number(props.rawTimestampValue);
      return Number.isFinite(num) ? num : 0;
    } catch {
      return 0;
    }
  })();
  const timestamp = new Date(timestampSeconds * 1000);
  try {
    format(timestamp, 'LLL d, y');
    format(timestamp, 'h:mm:ss aaa');
    format(timestamp, 'z');
  } catch {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          This value can't be formatted as a timestamp
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span>{format(timestamp, 'LLL d, y')}</span>
      <span className="text-zinc-400">|</span>
      <span>
        {format(timestamp, props.excludeSeconds ? 'h:mm aaa' : 'h:mm:ss aaa')}
      </span>
      <Badge variant="secondary" className="text-xs">
        {format(timestamp, 'z')}
      </Badge>
    </div>
  );
}

/**
 * Display a raw numeric parameter with formatting options
 */
export function RawValueNumber(props: {
  paramTreeNode: ParamTreeNode;
  depth: number;
  inListOrObject?: boolean;
  methodName?: string;
  paramName?: string;
  networkId: NetworkType;
}) {
  const rawValue = props.paramTreeNode?.rawValue;
  // Safely convert rawValue to number for timestamp detection (use 0 if too large)
  const rawValueAsNumber = (() => {
    try {
      if (typeof rawValue === 'number') return rawValue;
      if (typeof rawValue === 'string') {
        const num = Number(rawValue);
        return Number.isFinite(num) ? num : 0;
      }
      // BigInt values might be too large for Number - return 0 (not a timestamp)
      return 0;
    } catch {
      return 0;
    }
  })();
  const timestampLikelihood: TIMESTAMP_LIKELIHOOD = isLikelyAnEpochTime(
    rawValueAsNumber,
    props.paramName ?? '',
    props.methodName ?? ''
  );

  const [decimals, setDecimals] = useState<number>(0);
  const [formatType, setFormatType] = useState<'number' | 'datetime'>(
    timestampLikelihood === TIMESTAMP_LIKELIHOOD.LIKELY ? 'datetime' : 'number'
  );

  const handleSetFormatting = (
    newFormatType: 'number' | 'datetime',
    newDecimals?: number
  ) => {
    setFormatType(newFormatType);
    if (newDecimals !== undefined) {
      setDecimals(newDecimals);
    }
  };

  const formattedValue: string | React.ReactElement = (() => {
    // Case: despite rawType being a number, backend returned null
    if (rawValue === 'null' || rawValue === null || rawValue === undefined)
      return 'null';

    // Case: interpreting as datetime
    if (formatType === 'datetime') {
      const rawValueStr = rawValue?.toString() ?? '';
      // Case: automatically assumed datetime - explain this
      if (timestampLikelihood === TIMESTAMP_LIKELIHOOD.LIKELY) {
        return (
          <div className="flex flex-col gap-1">
            <FormattedTimestamp rawTimestampValue={rawValueStr} />
            <p className="text-xs text-zinc-500">
              Not a timestamp? Change how it's displayed in the dropdown
            </p>
          </div>
        );
      }
      // Case: user viewing as datetime, but we didn't auto-assume
      else {
        return <FormattedTimestamp rawTimestampValue={rawValueStr} />;
      }
    }

    const formattedNumber = formatNumber(
      props.paramTreeNode?.rawValue?.toString() as string,
      decimals
    );

    if (formattedNumber == null)
      return (
        <Alert variant="destructive">
          <AlertDescription>
            Unable to decode value, please report this to the Den team
          </AlertDescription>
        </Alert>
      );

    // Convert rawValue to bigint safely
    const rawValueAsBigInt: bigint = (() => {
      if (typeof rawValue === 'bigint') return rawValue;
      if (typeof rawValue === 'number') return BigInt(rawValue);
      if (typeof rawValue === 'string') return BigInt(rawValue);
      // Fallback for boolean or null
      return BigInt(0);
    })();

    const isMaxIntNum: boolean = isMaxInt(
      rawValueAsBigInt,
      props.paramTreeNode?.rawType.toString()
    );

    const isMinIntNum: boolean = isMinInt(
      rawValueAsBigInt,
      props.paramTreeNode?.rawType.toString()
    );

    // Case: maximum value for type
    if (isMaxIntNum)
      return (
        <div className="flex items-center gap-2">
          <Badge variant="secondary">Maximum Value</Badge>
          <Alert>
            <AlertDescription>
              Dapps often use this value to represent infinity
            </AlertDescription>
          </Alert>
        </div>
      );

    // Case: minimum value for type
    if (isMinIntNum)
      return (
        <div className="flex items-center gap-2">
          <Badge variant="secondary">Minimum Value</Badge>
          <Alert>
            <AlertDescription>
              Dapps often use this value to represent negative infinity
            </AlertDescription>
          </Alert>
        </div>
      );

    // Case: possible timestamp hint
    if (timestampLikelihood === TIMESTAMP_LIKELIHOOD.POSSIBLE)
      return (
        <div className="flex flex-col gap-1">
          <span>{formattedNumber}</span>
          <p className="text-xs text-zinc-500">
            Is this a timestamp? Change how it's displayed in the dropdown
          </p>
        </div>
      );

    return formattedNumber;
  })();

  const primaryComponent = (
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 w-full">
      <div className="break-all">{formattedValue}</div>
      <DecimalsButton
        decimals={decimals}
        formatType={formatType}
        hideTimestamp={false}
        onChange={(type, newDecimals) => {
          handleSetFormatting(type, newDecimals);
        }}
      />
    </div>
  );

  // Helper text showing unformatted value when decimals are applied
  const helperText =
    formatType === 'number' && decimals > 0 ? (
      <p className="text-xs text-zinc-500 mt-1">
        Value without conversion is {props.paramTreeNode.rawValue}
      </p>
    ) : null;

  // Case: nested in list/object, render inline
  if (props.inListOrObject && props.depth > 1)
    return (
      <div className="flex flex-col">
        {primaryComponent}
        {helperText}
      </div>
    );

  // Case: root level, wrap with border
  return (
    <div className="px-3 py-2 border border-zinc-200 border-l-2 border-l-zinc-900 bg-zinc-50 rounded-sm">
      {primaryComponent}
      {helperText}
    </div>
  );
}
